import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '@strivelabs/ui-core/v2';
import { useAppStore } from '@/store/appStore';
import { useChatWorkflow } from '@/hooks/useChatWorkflow';
import { getChatWorkflowInstanceId } from '@/utils/featureFlags';
import { startChatWorkflow } from '@/utils/chatHandoff';
import { logger } from '@/lib/logger';
import { ANALYTICS_EVENTS } from '@/lib/analytics/events';
import { useTrack } from '@/lib/analytics/useTrack';
import type { Task } from '@/types/task';

/** What a task thread is opened with — read by HomePage off the navigation state. */
export interface PendingTaskChat {
  task: Task;
}

/**
 * Starts a conversation about one task.
 *
 * Nothing is sent: the thread opens on the task's own empty state ("How can I
 * help with …?" plus the starter chips) with the task in the panel beside it,
 * and the first message is whatever the user picks. The task rides as chat
 * context (see `buildTaskChatContext`), so the agent has it before that
 * message rather than the message having to introduce it.
 *
 * The task is also bound to the execution as a TASK resource — the same
 * relationship a briefing thread has with its card — which is how the panel
 * comes back when the thread is reopened from the sidebar.
 */
export const useOpenTaskInChat = () => {
  const navigate = useNavigate();
  const { mutateAsync: triggerChatWorkflow } = useChatWorkflow();
  const workflowInstanceId = useAppStore(
    (state) => getChatWorkflowInstanceId(state.agentInstances),
  );
  const trackEvent = useTrack();

  return useCallback(async (task: Task, via: 'new_chat' | 'chat' = 'chat') => {
    if (!workflowInstanceId) {
      toast.error('Chat is unavailable right now.');
      return;
    }

    trackEvent(ANALYTICS_EVENTS.TASK_OPENED_IN_CHAT, { via });

    try {
      const executionId = await startChatWorkflow({
        triggerChatWorkflow,
        workflowInstanceId,
        resources: [ { resource_type: 'TASK', resource_id: task.id } ],
      });
      if (!executionId) return;
      const state: { pendingTaskChat: PendingTaskChat } = { pendingTaskChat: { task } };
      navigate(`/ui/chat/${executionId}`, { state });
    } catch (error) {
      logger(error as Error, { context: 'useOpenTaskInChat: could not start the chat' });
      toast.error('Could not start that chat. Please try again.');
    }
  }, [ navigate, trackEvent, triggerChatWorkflow, workflowInstanceId ]);
};
